/**
 * The one shared collector the Contact, Invoice and Job "charge a new card or
 * bank account" dialogs all mount. It:
 *
 *  - mints the tenant's processor collector (a Stripe SetupIntent + Elements,
 *    or the Payabli embedded component) for the requested `rail`;
 *  - on the bank rail, collects the staff-recorded ACH debit authorization the
 *    server requires before it will charge a bank account at all;
 *  - saves whatever the collector hands back as a stored payment method; and
 *  - hands the saved method's id back to the parent dialog via
 *    `onMethodReady`, which is the one that actually issues the charge.
 *
 * Neither `StripePaymentMethodSetupForm` nor `PayabliPaymentMethodSetupForm`
 * exposes an imperative submit — each renders its own submit button and there
 * is no ref to trigger it externally. So the collector's own button IS the
 * charge button: its `submitLabel` reads "Charge $<total>", not "Save".
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createUuid } from '@notifi/uuid/browser';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { AchAuthorizationFields } from '@/components/organisms/payments/AchAuthorizationFields';
import { useAchAuthorization } from '@/hooks/payments/useAchAuthorization';
import { ChargeServiceFeePreview } from '@/components/organisms/payments/ChargeServiceFeePreview';
import { ChargeTotalBreakdown } from '@/components/organisms/payments/ChargeTotalBreakdown';
import { resolveChargeTotals } from '@/components/organisms/payments/chargeTotals';
import { ServiceFeeQuoteUnavailable } from '@/components/organisms/payments/ServiceFeeQuoteUnavailable';
import { useCardServiceFeeQuote } from '@/hooks/payments/useCardServiceFeeQuote';
import {
  useContactPaymentMethodSetupIntent,
  useCreateContactPaymentMethod,
  useTokenizeContactPaymentMethod,
} from '@/hooks/contacts/useContactPaymentMethods';
import { usePayabliClientConfig, usePaymentSettings } from '@/hooks/settings/usePaymentSettings';
import { useHasPermission } from '@/hooks/usePermissions';
import type { PayabliClientConfig, PaymentSettings } from '@/types/settings';
import type { StaffChargeAuthorization, StaffChargeAuthorizationChannel } from '@/types/contact';

export type { StaffChargeAuthorization, StaffChargeAuthorizationChannel };

const StripePaymentMethodSetupForm = lazy(() =>
  import('@/components/organisms/payments/StripePaymentMethodSetupForm').then((module) => ({
    default: module.StripePaymentMethodSetupForm,
  })),
);

const PayabliPaymentMethodSetupForm = lazy(() =>
  import('@/components/organisms/payments/PayabliPaymentMethodSetupForm').then((module) => ({
    default: module.PayabliPaymentMethodSetupForm,
  })),
);

export type NewPaymentMethodChargeProps = {
  contactId: string;
  contactName: string;
  /**
   * The payer's record is still loading, so `contactName` is whatever fallback
   * the parent had to hand rather than the payer. Holds the bank authorization
   * until the real name is in — see `useAchAuthorization`.
   */
  payerNameLoading?: boolean;
  /** Whether the name we have is the payer's — see `useAchAuthorization`. */
  payerNameResolved?: boolean;
  contactEmail?: string | null;
  rail: 'card' | 'ach';
  /** The base being charged — what the card service fee is quoted on. */
  amount: number;
  /**
   * A tip charged on top of `amount`. Never surcharged (the server adds the
   * fee on the base only), but part of what leaves the customer's account, so
   * it is in the button total and the bank authorization. Defaults to 0.
   */
  tipAmount?: number;
  /** Ids of methods the contact already had on file, before this charge. */
  existingMethodIds: string[];
  onMethodReady: (input: {
    paymentMethodId: string;
    /**
     * The saved method's provider-side id (Stripe `pm_…`, Payabli stored
     * method). A charge answer stamps the id of the method it charged, so the
     * dialog can tell a replay of an earlier attempt from a charge of this one.
     */
    providerPaymentMethodId?: string | null;
    removeAfterCharge: boolean;
    authorization?: StaffChargeAuthorization;
    /**
     * What the customer is debited before any card fee: the base after the
     * tenant's ACH/check discount (if the server quoted one), plus the tip.
     * The figure the "Charge $X" button and the bank authorization name, less
     * the fee — for the dialog's success copy.
     */
    chargedAmount: number;
  }) => Promise<void>;
  /**
   * The amount this charge will record against invoices: the base after any
   * ACH/check discount — never the tip or a card fee. Null while the quote
   * that decides it is loading or has failed. Reported whenever it changes,
   * so a parent previewing where the payment lands previews what applies.
   */
  onAppliedAmountChange?: (amount: number | null) => void;
  disabled?: boolean;
};

/** What saving the entered method hands back, as far as the charge needs it. */
type SavedMethod = { id: string; providerPaymentMethodId?: string | null };

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

/**
 * Which rails a tenant accepts, resolved the same way `ContactPaymentMethodsSection`
 * does: Payabli tenants fail closed to whatever the embedded client config says
 * (an absent flag must never default to enabled); Stripe tenants preserve the
 * historically-supported card rail and only offer ACH once explicitly turned on.
 */
export function railAvailability(
  settings?: Pick<PaymentSettings, 'primaryProcessor' | 'acceptCreditCards' | 'acceptAch'> | null,
  payabliConfig?: Pick<PayabliClientConfig, 'acceptCard' | 'acceptACH'> | null,
): { card: boolean; ach: boolean } {
  if (settings?.primaryProcessor === 'payabli') {
    return {
      card: payabliConfig?.acceptCard === true,
      ach: payabliConfig?.acceptACH === true,
    };
  }
  return {
    card: settings?.acceptCreditCards !== false,
    ach: settings?.acceptAch === true,
  };
}

export function NewPaymentMethodCharge({
  contactId,
  contactName,
  payerNameLoading = false,
  payerNameResolved = true,
  contactEmail,
  rail,
  amount,
  tipAmount = 0,
  existingMethodIds,
  onMethodReady,
  onAppliedAmountChange,
  disabled,
}: NewPaymentMethodChargeProps) {
  const tip = Number.isFinite(tipAmount) && tipAmount > 0 ? tipAmount : 0;
  // `GET /api/settings/payments` requires `company:view`. A role without it
  // (a custom role; the baseline staff roles hold it) can never open this
  // collector, so it isn't asked, and a server refusal reads the same way:
  // a lack of access, not a failure worth another try.
  const canViewPaymentSettings = useHasPermission('company:view');
  const paymentSettingsQuery = usePaymentSettings({ enabled: canViewPaymentSettings });
  const paymentSettingsNoAccess =
    !canViewPaymentSettings || paymentSettingsQuery.error?.status === 403;
  // Nothing processor-specific happens until the tenant's settings say which
  // processor it is. Guessing Stripe while they load asked a Payabli tenant's
  // Stripe account for a SetupIntent, which it refuses ("processes cards with
  // Payabli").
  const processorKnown = Boolean(paymentSettingsQuery.data);
  const isPayabli = paymentSettingsQuery.data?.primaryProcessor === 'payabli';
  const payabliConfigQuery = usePayabliClientConfig({ enabled: isPayabli });

  const createPaymentMethod = useCreateContactPaymentMethod(contactId);
  const tokenizePaymentMethod = useTokenizeContactPaymentMethod(contactId);

  const [saveChecked, setSaveChecked] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Bumped when a charge on a just-saved method fails, to start collection
  // over: a new Stripe SetupIntent (the confirmed one can't be confirmed
  // again) or a freshly initialised Payabli component.
  const [collectAttempt, setCollectAttempt] = useState(0);

  // The Stripe SetupIntent for the requested rail. Payabli mounts its own
  // component from its client-config query instead, so nothing to mint there.
  //
  // A query keyed by this collector's own request key, which is also the
  // create's `Idempotency-Key` — see `useContactPaymentMethodSetupIntent` for
  // why this is not a mutation fired from an effect (StrictMode's replayed
  // mount effect sent a duplicate create that the server refused 409). A rail
  // switch is a new key, so a new intent; switching back reuses the first. A
  // new attempt is a new key too.
  const [collectorId] = useState(createUuid);
  const setupIntentQuery = useContactPaymentMethodSetupIntent(contactId, rail, {
    requestKey: `seti-${collectorId}-${rail}-${collectAttempt}`,
    enabled: processorKnown && !isPayabli,
  });
  const setupIntent = setupIntentQuery.data;

  // The server quotes the exact fee this charge will add — never computed
  // here. There is no saved method yet to quote off of, so this passes the
  // coarse rail rather than a stored method's `type`.
  const feeQuote = useCardServiceFeeQuote({
    amount,
    paymentMethod: rail === 'ach' ? 'ACH' : 'CARD',
    provider: isPayabli ? 'PAYABLI' : 'STRIPE',
    enabled: amount > 0 && processorKnown,
  });
  // A quote still in flight describes a different charge than the button
  // would send — block submit until the two agree, matching the saved-method
  // collect flow's `collectFeeQuoteStale` guard.
  const feeQuoteStale = feeQuote.isFetching;
  // No answer at all is worse than a stale one: a missing quote understates a
  // card's service fee and overstates a discounted bank debit in the
  // authorization. Nothing is charged until the server has priced it (same
  // rule as `CollectDepositModal`). No quote is fetched at $0, and the
  // dialogs disable the collector there anyway.
  const feeQuoteReady = !(amount > 0) || (Boolean(feeQuote.data) && !feeQuote.isFetching);
  const feeQuoteFailed = amount > 0 && feeQuote.isError && !feeQuote.isFetching;

  // The server takes the tenant's ACH/check discount off a bank debit's base
  // (never off the tip) and quotes the discounted base as `chargeCents`; a card
  // under the same program is quoted with nothing off. Shared with the job's
  // Record payment dialog, which states the same figures before its charge.
  const { discountedBase, debitAmount, chargeTotal } = resolveChargeTotals(
    feeQuote.data,
    amount,
    tip,
  );
  // What applies to invoices is the base the payment records: discounted on a
  // bank debit, whole on a card. Unknown until the server has priced it.
  const appliedAmount =
    amount > 0 && feeQuoteReady && !feeQuoteFailed
      ? rail === 'ach'
        ? Math.round(discountedBase * 100) / 100
        : amount
      : null;
  // Keyed on the value alone: a parent passing a fresh callback each render
  // must not be re-notified (or loop through its own state update).
  const onAppliedAmountChangeRef = useRef(onAppliedAmountChange);
  useEffect(() => {
    onAppliedAmountChangeRef.current = onAppliedAmountChange;
  });
  useEffect(() => {
    onAppliedAmountChangeRef.current?.(appliedAmount);
  }, [appliedAmount]);
  const chargeLabel = `Charge ${formatCurrency(chargeTotal)}`;

  const achAuthorization = useAchAuthorization({
    contactName,
    payerNameLoading,
    payerNameResolved,
    debitAmount,
    enabled: rail === 'ach',
  });
  const authorizationReady = achAuthorization.ready;
  const collectorDisabled =
    Boolean(disabled) || isSubmitting || !feeQuoteReady || !authorizationReady;
  // The save/authorization inputs live above the collector and must lock once
  // the charge sequence has started, independent of the collector's own
  // enablement rules (fee-quote staleness, missing authorization, etc).
  const inputsDisabled = Boolean(disabled) || isSubmitting;

  /**
   * Charge a method this collector has just saved, through the dialog.
   *
   * The dialog owns the charge's outcome and its message (a decline, an
   * unknown outcome, a key mismatch). A failure here is therefore NOT the
   * form's "Unable to save payment method" — the save worked — so it is not
   * rethrown into the form. Instead collection starts over: the method just
   * entered has been used (Stripe won't confirm a succeeded SetupIntent
   * twice), so staff get a fresh form to enter a card or account again
   * without closing the dialog.
   */
  async function chargeSavedMethod(saved: SavedMethod) {
    try {
      await finish(saved);
    } catch {
      setIsSubmitting(false);
      setCollectAttempt((attempt) => attempt + 1);
    }
  }

  async function finish({ id: savedMethodId, providerPaymentMethodId }: SavedMethod) {
    const removeAfterCharge = !saveChecked && !existingMethodIds.includes(savedMethodId);
    const authorization = achAuthorization.build();
    await onMethodReady(
      authorization
        ? {
            paymentMethodId: savedMethodId,
            providerPaymentMethodId,
            removeAfterCharge,
            authorization,
            chargedAmount: debitAmount,
          }
        : {
            paymentMethodId: savedMethodId,
            providerPaymentMethodId,
            removeAfterCharge,
            chargedAmount: debitAmount,
          },
    );
  }

  async function handleStripeComplete(paymentMethodId: string) {
    if (!setupIntent) return;
    setIsSubmitting(true);
    let saved: SavedMethod;
    try {
      saved = await createPaymentMethod.mutateAsync({
        provider: 'STRIPE',
        providerPaymentMethodId: paymentMethodId,
        providerCustomerId: setupIntent.providerCustomerId,
        label: null,
        cardholderName: null,
        makeDefault: false,
      });
    } catch (error) {
      // A genuine save failure: the form shows it.
      setIsSubmitting(false);
      throw error;
    }
    await chargeSavedMethod(saved);
  }

  async function handlePayabliComplete(result: {
    temporaryToken: string;
    type: string;
    brand?: string | null;
    last4?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
  }) {
    setIsSubmitting(true);
    let saved: SavedMethod;
    try {
      saved = await tokenizePaymentMethod.mutateAsync({
        temporaryToken: result.temporaryToken,
        type: result.type,
        last4: result.last4 ?? '',
        brand: result.brand ?? null,
        expMonth: result.expMonth ?? null,
        expYear: result.expYear ?? null,
        label: null,
        cardholderName: null,
        makeDefault: false,
      });
    } catch (error) {
      // A genuine save failure: the form shows it.
      setIsSubmitting(false);
      throw error;
    }
    await chargeSavedMethod(saved);
  }

  const loadingSpinner = (
    <div className="flex items-center justify-center rounded-lg border border-border p-8">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      {rail === 'ach' && processorKnown && !isPayabli ? (
        <Alert variant="warning">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Never ask a customer for their bank login</AlertTitle>
          <AlertDescription>
            The customer must enter their own online banking login. Never ask a customer for it.
          </AlertDescription>
        </Alert>
      ) : null}

      {rail === 'ach' ? (
        <AchAuthorizationFields
          authorization={achAuthorization}
          disabled={inputsDisabled}
          showNoEmailWarning={!contactEmail}
          noAccessMessage="You don't have access to the company name needed to charge a new bank account: the authorization has to name your business. Ask an admin."
        />
      ) : null}

      <label className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm">
        <Checkbox
          checked={saveChecked}
          onCheckedChange={(checked) => setSaveChecked(Boolean(checked))}
          disabled={inputsDisabled}
        />
        Customer agrees to keep this payment method on file.
      </label>

      {!feeQuoteStale ? (
        <ChargeServiceFeePreview quote={feeQuote.data} baseAmount={amount} tipAmount={tip} />
      ) : null}
      {feeQuoteFailed ? (
        <ServiceFeeQuoteUnavailable onRetry={() => void feeQuote.refetch()} />
      ) : null}

      {/* The fee preview speaks about the base only; with a tip on top, spell
          out how base, discount, tip and fee add up to the button's total. */}
      {!feeQuoteStale ? (
        <ChargeTotalBreakdown
          quote={feeQuote.data}
          baseAmount={amount}
          tipAmount={tip}
          data-testid="new-method-charge-breakdown"
        />
      ) : null}

      {!processorKnown ? (
        paymentSettingsNoAccess ? (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Can&apos;t open the payment form</AlertTitle>
            <AlertDescription>
              You don&apos;t have access to payment settings needed to charge a new card or bank
              account. Ask an admin.
            </AlertDescription>
          </Alert>
        ) : paymentSettingsQuery.isError ? (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Unable to load payment settings</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>The payment form can&apos;t open until they load.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void paymentSettingsQuery.refetch()}
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          loadingSpinner
        )
      ) : isPayabli ? (
        payabliConfigQuery.isPending ? (
          loadingSpinner
        ) : payabliConfigQuery.data?.publicToken && payabliConfigQuery.data.entryPoint ? (
          <Suspense fallback={loadingSpinner}>
            <PayabliPaymentMethodSetupForm
              // A new attempt re-initialises the embedded component.
              key={`payabli-${collectAttempt}`}
              publicToken={payabliConfigQuery.data.publicToken}
              entryPoint={payabliConfigQuery.data.entryPoint}
              mode={payabliConfigQuery.data.mode}
              methods={{ card: rail === 'card', ach: rail === 'ach' }}
              customerData={{
                customerNumber: contactId,
                firstName: contactName.split(' ')[0] || undefined,
                lastName: contactName.split(' ').slice(1).join(' ') || undefined,
                billingEmail: contactEmail ?? undefined,
              }}
              submitLabel={chargeLabel}
              disabled={collectorDisabled}
              onComplete={handlePayabliComplete}
            />
          </Suspense>
        ) : (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Payabli isn&apos;t ready for payment-method capture</AlertTitle>
            <AlertDescription>
              {payabliConfigQuery.data && !payabliConfigQuery.data.connected
                ? 'Connect a Payabli paypoint in Settings → Payments first.'
                : 'The Payabli payment component is not configured yet. Contact support if this persists.'}
            </AlertDescription>
          </Alert>
        )
      ) : setupIntentQuery.isPending ? (
        loadingSpinner
      ) : setupIntent ? (
        <Suspense fallback={loadingSpinner}>
          <StripePaymentMethodSetupForm
            // Elements can't swap its clientSecret once mounted: a new intent
            // (a rail switch) is a new form.
            key={setupIntent.clientSecret}
            clientSecret={setupIntent.clientSecret}
            publishableKey={setupIntent.publishableKey}
            stripeAccountId={setupIntent.stripeAccountId}
            submitLabel={chargeLabel}
            disabled={collectorDisabled}
            onComplete={handleStripeComplete}
          />
        </Suspense>
      ) : (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Unable to start {rail === 'ach' ? 'bank account' : 'card'} setup</AlertTitle>
          <AlertDescription>
            {setupIntentQuery.error?.message ??
              `Stripe ${rail === 'ach' ? 'ACH' : 'card'} setup failed.`}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
